import { html } from '@crossiety/lit-components';
import BaseMenu from '@crossiety/lit-components/components/base_menu/element.js';
import '@crossiety/lit-components/components/dot_menu/element.js';
import translations from './translations.js';

/**
 * Dot dot menu for platform member
 *
 * @prop resourceName - Member name
 * @prop memberId - Id of the platform member
 * @prop reportOn - Should show report option in menu?
 * @prop blockOn - Should show block option in menu?
 * @prop unblockOn - Should show unblock option in menu?
 *
 */

export default class MemberMenu extends BaseMenu {
  static properties = {
    resourceName: { type: String },
    memberId: { type: Number },
    reportOn: { type: Boolean },
    unblockOn: { type: Boolean },
    blockOn: { type: Boolean },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return this.memberId;
  }

  /** @internal */
  get baseUrl() {
    return `/profile/blockings/${this.memberId}`;
  }

  /** @internal */
  get reportUrl() {
    return `/complaints/new?complaint[complainable_id]=${this.memberId}&complaint[complainable_type]=User`;
  }

  /** @internal */
  get unblockUrl() {
    return `${this.baseUrl}/unblock_user`;
  }

  /** @internal */
  get blockUrl() {
    return `${this.baseUrl}/block_user`;
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.report'),
      action: this.actionFor('memberExecJSFromResponse', [this.reportUrl]),
      icon: 'exclamation-triangle',
      partName: 'report',
    };
  }

  /** @internal */
  get blockItem() {
    return {
      title: this.t('actions.block'),
      action: this.actionFor('memberBlock', [this.blockUrl, 'block']),
      icon: 'ban',
      partName: 'block',
    };
  }

  /** @internal */
  get unblockItem() {
    return {
      title: this.t('actions.unblock'),
      action: this.actionFor('memberPost', [this.unblockUrl, 'unblock']),
      icon: 'ban:solid',
      partName: 'unblock',
    };
  }

  /** @internal */
  get items() {
    return [
      this.reportOn && this.reportItem,
      this.blockOn && this.blockItem,
      this.unblockOn && this.unblockItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.resourceName,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-member-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  memberExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  async memberPost(url, action) {
    try {
      await this.commonActions.post(url);
      this.dispatchEvent(
        new Event('member-updated', { bubbles: true, composed: true })
      );
      this.commonActions.displaySnackBarSuccess(
        this.t(`snack.${action}_success`)
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async memberBlock() {
    if (!this.commonActions.confirmDialog(this.t('confirm_dialog_msg.block')))
      return false;
    await this.memberPost(this.blockUrl, 'block');
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <slot slot="trigger">
          <button class="trigger" aria-label="toggle button">
            <ef-icon name="ellipsis-h"></ef-icon>
          </button>
        </slot>
      </ef-dot-menu>
    `;
  }
}

MemberMenu.registerTagName('xs-member-menu');
MemberMenu.registerCommonActions('xs-member-menu');

customElements.get('xs-member-menu') ||
  customElements.define('xs-member-menu', MemberMenu);
